// src/components/donors/ViewDonorModal.js

import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FiX, FiMail, FiPhone, FiMapPin, FiGlobe, FiDollarSign, FiCalendar, FiFileText, FiBriefcase, FiTag, FiTarget, FiClock, FiAlertCircle, FiFolder, FiSettings } from "react-icons/fi";
import { format } from 'date-fns';

/**
 * A reusable component to display a detail item with an icon.
 * @param {React.ReactNode} icon - The icon component.
 * @param {string} label - The label for the detail.
 * @param {string|number|string[]} value - The value of the detail.
 */
const DetailItem = ({ icon, label, value }) => {
    // Handle array values by joining them with a comma
    const displayValue = Array.isArray(value) ? value.join(", ") : value;
    const finalValue = displayValue || "N/A";

    // Handle special case for 'N/A' or empty value
    const valueClass = finalValue === "N/A" ? "text-gray-400 font-normal" : "font-semibold text-gray-800";

    return (
        <div className="flex items-start py-2">
            <div className="text-orange-500 mr-4 mt-1">{icon}</div>
            <div>
                <p className="text-sm text-gray-500">{label}</p>
                <p className={valueClass}>{finalValue}</p>
            </div>
        </div>
    );
};

/**
 * A modal to display comprehensive, read-only details of a donor.
 * @param {string} donorId - The ID of the donor to view.
 * @param {Function} closeModal - Function to close the modal.
 */
export default function ViewDonorModal({ donorId, closeModal }) {
    const modalRef = useRef(null);
    const [donor, setDonor] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    // Handle click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                closeModal();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [closeModal]);

    /**
     * Fetches the specific donor's data from the backend.
     */
    const fetchDonorDetails = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Authentication token not found.");

            const API_URL = "https://ims.holdthechild.net";
            const response = await axios.get(`${API_URL}/api/donors/${donorId}`, {
                headers: { "Authorization": `Bearer ${token}` },
            });

            // Assuming the backend returns the full donor object in response.data.data
            setDonor(response.data.data);

        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Failed to fetch donor details.";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [donorId]);

    useEffect(() => {
        fetchDonorDetails();
    }, [fetchDonorDetails]);

    /**
     * Formats a number to a US Dollar (USD) currency string.
     * @param {number} amount - The amount to format.
     */
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    // Helper function to concatenate the address fields
    const formatAddress = (address) => {
        if (!address) return null;
        const parts = [address.street, address.city, address.state, address.country, address.postalCode].filter(Boolean);
        return parts.length > 0 ? parts.join(", ") : null;
    }

    if (loading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative transform transition-all duration-300 ease-out flex flex-col items-center justify-center p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mb-4"></div>
                    <p className="text-gray-700 font-medium">Loading donor details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative transform transition-all duration-300 ease-out p-6">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                            <FiX className="h-6 w-6 text-red-600" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
                        <p className="text-sm text-gray-500 mb-4">{error}</p>
                        <button 
                            onClick={closeModal} 
                            className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!donor) return null; // Should not happen with the above checks, but as a safeguard

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
            <div 
                ref={modalRef}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl relative transform transition-all duration-300 ease-out max-h-[90vh] flex flex-col"
            >
                {/* Fixed Header with Orange Theme */}
                <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-t-2xl p-4 sm:p-6 border-b border-orange-500 flex-shrink-0">
                    <div className="flex justify-between items-center">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                <FiDollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                            </div>
                            <div>
                                <h2 className="text-lg sm:text-xl font-bold text-black">
                                    Donor Profile: <span className="text-gray-900">{donor.displayName}</span>
                                </h2>
                                <div className="flex items-center mt-1 text-black text-sm">
                                    <FiFolder className="w-3 h-3 mr-1" />
                                    <span>Programs Department</span>
                                </div>
                            </div>
                        </div>
                        <button 
                            onClick={closeModal} 
                            className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
                        >
                            <FiX size={20} />
                        </button>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-grow overflow-y-auto p-4 sm:p-6">
                    {/* --- Main Details Section --- */}
                    <section className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-6">
                        <DetailItem icon={<FiBriefcase size={20} />} label="Donor Type" value={donor.donorType} />
                        <DetailItem icon={<FiTag size={20} />} label="Donor Category" value={donor.donorCategory} />
                        {donor.donorType === "individual" ? (
                            <DetailItem icon={<FiBriefcase size={20} />} label="Full Name" value={donor.name} />
                        ) : (
                            <DetailItem icon={<FiBriefcase size={20} />} label="Organization Name" value={donor.organization} />
                        )}
                        <DetailItem icon={<FiTarget size={20} />} label="Contact Person" value={donor.contactPerson} />
                    </section>

                    <div className="border-t border-gray-200 my-6"></div>

                    {/* --- Contact and Address Section --- */}
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                        <FiMail className="w-5 h-5 mr-2 text-orange-500" />
                        Contact & Location
                    </h3>
                    <section className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-6">
                        <DetailItem icon={<FiMail size={20} />} label="Primary Email" value={donor.email} />
                        <DetailItem icon={<FiMail size={20} />} label="Alternate Email" value={donor.alternateEmail} />
                        <DetailItem icon={<FiPhone size={20} />} label="Primary Phone" value={donor.phone?.primary} />
                        <DetailItem icon={<FiPhone size={20} />} label="Secondary Phone" value={donor.phone?.secondary} />
                        <DetailItem icon={<FiMapPin size={20} />} label="Address" value={formatAddress(donor.address)} />
                        <DetailItem icon={<FiGlobe size={20} />} label="Country" value={donor.address?.country} />
                    </section>

                    <div className="border-t border-gray-200 my-6"></div>

                    {/* --- Donation Overview Section (Leverages Mongoose Virtuals) --- */}
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                        <FiDollarSign className="w-5 h-5 mr-2 text-orange-500" />
                        Donation Overview
                    </h3>
                    <section className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-6">
                        <div className="bg-gray-100 p-4 rounded-lg">
                            <DetailItem icon={<FiDollarSign size={20} />} label="Total Donations" value={formatCurrency(donor.totalDonations)} />
                        </div>
                        <div className="bg-gray-100 p-4 rounded-lg">
                            <DetailItem icon={<FiCalendar size={20} />} label="Last Donation Date" value={donor.lastDonationDate ? format(new Date(donor.lastDonationDate), 'dd MMM yyyy') : "N/A"} />
                        </div>
                    </section>

                    <div className="border-t border-gray-200 my-6"></div>

                    {/* --- Detailed Donation History Section --- */}
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                        <FiDollarSign className="w-5 h-5 mr-2 text-orange-500" />
                        Donation History
                    </h3>
                    <section className="bg-gray-50 p-4 rounded-lg max-h-60 overflow-y-auto mb-6">
                        {donor.donationHistory && donor.donationHistory.length > 0 ? (
                            <div className="space-y-4">
                                {donor.donationHistory.map((donation, index) => (
                                    <div key={index} className="border-b border-gray-200 last:border-b-0 pb-4">
                                        <p className="font-medium text-gray-900 flex justify-between">
                                            <span>{formatCurrency(donation.amount)} to {donation.project?.projectTitle || 'N/A'}</span>
                                            <span className="text-sm text-gray-500">{format(new Date(donation.date), 'dd MMM yyyy')}</span>
                                        </p>
                                        <p className="text-sm text-gray-600 mt-1">
                                            Payment Method: <span className="capitalize">{donation.paymentMethod}</span>
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 text-center">No donation history available.</p>
                        )}
                    </section>

                    <div className="border-t border-gray-200 my-6"></div>

                    {/* --- Other Details and Notes Section --- */}
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                        <FiSettings className="w-5 h-5 mr-2 text-orange-500" />
                        Preferences & Notes
                    </h3>
                    <section className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-6">
                        <DetailItem icon={<FiMail size={20} />} label="Communication Preference" value={donor.preferences?.communication} />
                        <DetailItem icon={<FiClock size={20} />} label="Communication Frequency" value={donor.preferences?.frequency} />
                        <div className="md:col-span-2">
                            <DetailItem icon={<FiFileText size={20} />} label="General Notes" value={donor.notes} />
                        </div>
                        <div className="md:col-span-2">
                            <DetailItem icon={<FiAlertCircle size={20} />} label="Status" value={donor.status} />
                        </div>
                    </section>

                    <div className="flex justify-end mt-8">
                        <button 
                            onClick={closeModal} 
                            className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}